import { saveAs } from "file-saver";
import { ActionFunctionArgs, LoaderFunctionArgs, redirect } from "react-router-dom";

import { getCurrentWeekRange, myAxios } from "src/components/common/functions";
import { appendField } from "../common/FilterFrame/FilterField";
import { buildDefaultState } from "../common/FilterFrame/FilterModal";
import { extraFields } from "./PerDiemList";
import { fields } from "./PerDiemFilterForm";

export async function perDiemsLoader({ request }: LoaderFunctionArgs) {
    const url = new URL(request.url);
    const pageParam = url.searchParams.get("page");
    const limitParam = url.searchParams.get("limit");
    const week = getCurrentWeekRange();
    const startParam = url.searchParams.get("createdAtFrom");
    const endParam = url.searchParams.get("createdAtTo");
    let shouldRedirect = false;

    for (const [key] of url.searchParams) {
        if (![...Object.keys(buildDefaultState(fields)), ...extraFields].includes(key)) {
            url.searchParams.delete(key);
            shouldRedirect = true;
        }
    }
    if (!pageParam) {
        url.searchParams.set("page", "1");
        shouldRedirect = true;
    }
    if (!limitParam) {
        url.searchParams.set("limit", "10");
        shouldRedirect = true;
    }
    if (!startParam) {
        url.searchParams.set("createdAtFrom", week.start);
        shouldRedirect = true;
    }
    if (!endParam) {
        url.searchParams.set("createdAtTo", week.end);
        shouldRedirect = true;
    }
    if (shouldRedirect) {
        throw redirect(url.pathname + "?" + url.searchParams.toString());
    }

    const page = Number(url.searchParams.get("page"));
    const limit = Number(url.searchParams.get("limit"));
    const formData = new FormData();
    formData.append("page", String(page));
    formData.append("limit", String(limit));
    for (const field of fields) {
        appendField(formData, url.searchParams, field);
    }

    const res = await myAxios({ fileName: "perDiem/readPerDiems.php", formData });
    if (!res) {
        throw redirect("..");
    }
    return res.data;
}

export async function perDiemLoader({ params }: LoaderFunctionArgs) {
    const { id } = params;
    if (!id) {
        throw redirect("/PerDiem");
    }
    const formData = new FormData();
    formData.append("id", id);
    const res = await myAxios({
        fileName: "perDiem/readPerDiem.php",
        formData,
    });
    if (!res || !res.data) {
        throw redirect("..");
    }
    return {
        perDiem: res.data,
    };
}

const urlMap = {
    Add: "perDiem/createPerDiem.php",
    Edit: "perDiem/updatePerDiem.php",
};

export async function perDiemAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });

    const formData = await request.formData();
    const action = String(formData.get("action") ?? "");
    const url = urlMap[action as keyof typeof urlMap];
    if (!url) {
        window.alert("Invalid per diem action.");
        return null;
    }
    if (!formData.get("requesterId")) {
        window.alert("Please select requester.");
        return null;
    }
    if (!formData.get("projectId")) {
        window.alert("Please select project.");
        return null;
    }
    if (!formData.get("approverId")) {
        window.alert("Please select approver.");
        return null;
    }

    const validationRes = await myAxios({
        fileName: "perDiem/validatePerDiem.php",
        formData,
        signal: controller.signal,
    });
    if (!validationRes) return null;

    const id = String(formData.get("id") ?? "");
    const startDate = String(formData.get("startDate") ?? "");
    const endDate = String(formData.get("endDate") ?? "");
    const ranges = Array.isArray(validationRes.data)
        ? validationRes.data.filter((item: { id: number }) => String(item.id) !== id)
        : [];
    for (const range of ranges as { projectName: string; startDate: string; endDate: string }[]) {
        const before = endDate < range.startDate;
        const after = startDate > range.endDate;
        if (!(before || after)) {
            window.alert(`Time conflict with ${range.projectName}: ${range.startDate} to ${range.endDate}`);
            return null;
        }
    }

    const res = await myAxios({
        fileName: url,
        formData,
        signal: controller.signal,
    });
    if (!res) return null;

    if (action === "Add") {
        return redirect(`/PerDiem/${res.data.id}`);
    }
    return redirect("..");
}

async function runSimpleAction(request: Request, fileName: string, fallback = "..") {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const formData = await request.formData();
    if (!formData.get("id")) {
        throw new Response("Missing id", { status: 400 });
    }
    const res = await myAxios({
        fileName,
        formData,
        signal: controller.signal,
    });
    if (!res) throw redirect(fallback);
}

export async function deletePerDiemAction({ request }: ActionFunctionArgs) {
    await runSimpleAction(request, "perDiem/deletePerDiem.php", "../..");
    throw redirect("../..");
}

export async function voidPerDiemAction({ request }: ActionFunctionArgs) {
    await runSimpleAction(request, "perDiem/voidPerDiem.php");
    throw redirect("..");
}

export async function validatePerDiemAction({ request }: ActionFunctionArgs) {
    await runSimpleAction(request, "perDiem/validatePerDiemRecord.php");
    throw redirect("..");
}

export async function notifyPerDiemAction({ request }: ActionFunctionArgs) {
    await runSimpleAction(request, "perDiem/notifyPerDiem.php");
    throw redirect("..");
}

export async function approvePerDiemAction({ request }: ActionFunctionArgs) {
    await runSimpleAction(request, "perDiem/approvePerDiem.php");
    throw redirect("..");
}

export async function paidPerDiemAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const url = new URL(request.url);
    const formData = await request.formData();
    if (!formData.get("id")) {
        throw new Response("Missing id", { status: 400 });
    }
    const res = await myAxios({
        fileName: "perDiem/paidPerDiem.php",
        formData,
        signal: controller.signal,
    });
    if (!res) {
        throw redirect(`..?${url.searchParams.toString()}`);
    }
    throw redirect(`..?${url.searchParams.toString()}`);
}

export async function perDiemExportAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const url = new URL(request.url);
    const formData = await request.formData();
    for (const field of fields) {
        appendField(formData, url.searchParams, field);
    }
    const res = await myAxios({
        fileName: "perDiem/exportPerDiems.php",
        formData,
        responseType: "blob",
        signal: controller.signal,
    });
    if (!res) {
        throw redirect(`..?${url.searchParams.toString()}`);
    }
    const disposition = res.headers["content-disposition"];
    let filename = "Per Diems.xlsx";
    if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) filename = decodeURIComponent(match[1]);
    }
    saveAs(
        new Blob([res.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        filename
    );
    throw redirect(`..?${url.searchParams.toString()}`);
}
